import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import FetchModule from '../../Share/Network/FetchModule';

import AttendanceChart from './AttendanceChart';
import ClassAttendanceList from './ClassAttendanceList';
import AttendanceRankingList from './AttendanceRankingList';

import LoadingAnimation from '../../../Animation/NewLoading.json';

import { ReactComponent as CheckSvg } from '../../../image/Check.svg';

export default function AttendanceBox(props) {
  const {
    today,
    startDate,
    endDate,
    calcDateText,

    attendanceTotal,
    attendanceRanking,
    totalMonthAttendanceCount,

    allMembers,
    isLoadingAllMember,

    selectedPartnerGym,
    isOpenPartnerGymSales,
    isAttendanceLoading,
    setIsAttendanceLoading,

    frequencyData,
    isFrequencyLoading,
  } = props;

  const history = useHistory();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [viewMode, setViewMode] = useState('monthly');
  const [isCountOncePerDay, setIsCountOncePerDay] = useState(true);

  const [yearlyAttendanceData, setYearlyAttendanceData] = useState([]);

  // allMembers의 usages를 timestamp로 미리 파싱한 Map //
  const parsedAllMembers = useMemo(() => {
    return allMembers.map(m => ({
      memberId: m.memberId,
      usages: (m.usages || []).map(u => ({
        start: moment(u.startDate).startOf('day').valueOf(),
        end: moment(u.endDate).startOf('day').valueOf(),
      }))
    }));
  }, [allMembers]);

  // 출석 데이터 로딩 완료 처리 //
  useEffect(() => {
    if (!startDate || !endDate) return;

    let isLatest = true;

    if (isLatest) {
      setTimeout(() => {
        setIsAttendanceLoading(false);
      }, 1000);
    }

    return () => {
      isLatest = false;
    }
  }, [isAttendanceLoading, attendanceTotal, startDate, endDate]);

  // 월간 차트용 연간 출석 데이터 호출 //
  useEffect(() => {
    let isLatest = true;

    const yearStart = moment(today.format('YYYY') + '-01-01T00:00:00');
    const yearEnd = moment(today.format('YYYY') + '-12-31T23:59:59');

    if (selectedPartnerGym === undefined || selectedPartnerGym.isMyStore) {
      let requestData = {
        startDate: yearStart.format('YYYY-MM-DDTHH:mm:ss'),
        endDate: yearEnd.format('YYYY-MM-DDTHH:mm:ss')
      };

      const fetchModule = new FetchModule();
      (
        async () => {
          const responseData = await fetchModule.postDataVersion2('sales/monthly/attendance', 'POST', requestData);
           console.log(responseData.data);

          if (responseData.status === 200) {
            if (isLatest) {
              setYearlyAttendanceData(responseData.data);
            }
          }
          else if (responseData.status === 401) history.push('/');
          else {
            if (isLatest) {
              setYearlyAttendanceData([]);
            }
          }
        }
      )();
    }
    else {
      if (selectedPartnerGym.isOpenPartnerGymSales) {
        let requestData = {
          storeId: selectedPartnerGym.storeId,
          startDate: yearStart.format('YYYY-MM-DDTHH:mm:ss'),
          endDate: yearEnd.format('YYYY-MM-DDTHH:mm:ss')
        };

        const fetchModule = new FetchModule();
        (
          async () => {
            const responseData = await fetchModule.postDataVersion2('sales/monthly/attendance/partner_gym', 'POST', requestData);

            if (responseData.status === 200) {
              if (isLatest) {
                setYearlyAttendanceData(responseData.data);
              }
            }
            else if (responseData.status === 401) history.push('/');
            else {
              if (isLatest) {
                setYearlyAttendanceData([]);
              }
            }
          }
        )();
      }
      else {
        setYearlyAttendanceData([]);
      }
    }

    return () => { isLatest = false; }
  }, [today, selectedPartnerGym]);

  // 차트 데이터 생성 //
  const chartData = useMemo(() => {
    // 월간: 1월~12월 월별 출석률 //
    if (viewMode === 'monthly') {
      const monthLabels = [
        t('statistics.chart.month1'), t('statistics.chart.month2'), t('statistics.chart.month3'),
        t('statistics.chart.month4'), t('statistics.chart.month5'), t('statistics.chart.month6'),
        t('statistics.chart.month7'), t('statistics.chart.month8'), t('statistics.chart.month9'),
        t('statistics.chart.month10'), t('statistics.chart.month11'), t('statistics.chart.month12')
      ];

      const result = [];

      for (let month = 0; month < 12; month++) {
        const validCount = getMonthlyValidCount(today.year(), month + 1);
        const attendedCount = getMonthlyAttendedCount(yearlyAttendanceData, today.year(), month + 1, isCountOncePerDay);
        const rate = validCount === 0 ? 0 : (attendedCount / validCount) * 100;

        result.push({
          day: month + 1,
          label: monthLabels[month],
          tooltipLabel: monthLabels[month],
          rate: Math.round(rate * 10) / 10,
          attendees: attendedCount,
          validMembers: validCount
        });
      }

      return result;
    }
    // 일간: 해당 기간 일별 출석률 //
    else {
      if (!startDate || !endDate) return [];

      const result = [];
      const current = startDate.clone();

      while (current.isSameOrBefore(endDate, 'day')) {
        const { attendees, validMembers } = getDailyStats(attendanceTotal, current, isCountOncePerDay);
        const rate = validMembers === 0 ? 0 : (attendees / validMembers) * 100;

        result.push({
          day: current.date(),
          label: String(current.date()),
          tooltipLabel: current.date() + t('statistics.attendance.daySuffix'),
          rate: Math.round(rate * 10) / 10,
          attendees: attendees,
          validMembers: validMembers
        });

        current.add(1, 'day');
      }

      return result;
    }
  }, [viewMode, attendanceTotal, yearlyAttendanceData, startDate, endDate, isCountOncePerDay, parsedAllMembers, t]);

  // 랭킹 데이터 (월간 고정) //
  const processedAttendanceRanking = useMemo(() => {
    return isCountOncePerDay
      ? calculateOncePerDayRanking(attendanceRanking)
      : attendanceRanking;
  }, [attendanceRanking, isCountOncePerDay]);

  // 로딩 상태 //
  const isLoading = isAttendanceLoading || isFrequencyLoading || isLoadingAllMember;

  // 회원이 특정 날짜에 유효한지 확인 (timestamp 비교) //
  function isMemberValidOn(parsedMember, dayTs) {
    for (const u of parsedMember.usages) {
      if (u.start <= dayTs && u.end >= dayTs) return true;
    }

    return false;
  }

  // 회원의 특정 날짜 출석 횟수 계산 //
  function getMemberAttendanceOnDay(member, dayStr, oncePerDay) {
    if (oncePerDay) {
      for (const r of member.reserveAttendances) {
        if (r.classDate.substring(0, 10) === dayStr) return 1;
      }
      return 0;
    }

    let count = 0;

    for (const r of member.reserveAttendances) {
      if (r.classDate.substring(0, 10) === dayStr) count++;
    }

    return count;
  }

  // 일간 출석률: 출석자 수 및 유효 회원 수 계산 //
  function getDailyStats(attendanceMembers, day, oncePerDay) {
    const dayTs = moment(day).startOf('day').valueOf();
    const dayStr = moment(day).format('YYYY-MM-DD');
    let attendees = 0;
    let validMembers = 0;

    // 유효 회원 수: parsedAllMembers 기반 (timestamp 비교) //
    for (const m of parsedAllMembers) {
      if (isMemberValidOn(m, dayTs)) validMembers++;
    }

    // 출석자 수: 출석 데이터 기반 //
    for (const m of attendanceMembers) {
      attendees += getMemberAttendanceOnDay(m, dayStr, oncePerDay);
    }

    return { attendees, validMembers };
  }

  // 일간 평균 출석률: (1/D) Σ r_i × 100 //
  function calcDailyAverageRate(members, start, end, oncePerDay) {
    const now = moment();
    const lastDay = moment.min(moment(end), now);
    const current = moment(start).clone();
    let sumOfRates = 0;
    let dayCount = 0;

    while (current.isSameOrBefore(lastDay, 'day')) {
      const { attendees, validMembers } = getDailyStats(members, current, oncePerDay);

      if (validMembers > 0) {
        sumOfRates += attendees / validMembers;
        dayCount++;
      }

      current.add(1, 'day');
    }

    if (dayCount === 0) return 0;
    return (sumOfRates / dayCount) * 100;
  }

  // 월간 고유 유효회원 수: 해당 월에 1일 이상 유효했던 고유 회원 수 //
  function getMonthlyValidCount(year, month) {
    const mStartTs = moment({ year, month: month - 1, day: 1 }).startOf('day').valueOf();
    const mEndTs = moment({ year, month: month - 1, day: 1 }).endOf('month').startOf('day').valueOf();
    let count = 0;

    for (const m of parsedAllMembers) {
      for (const u of m.usages) {
        if (u.end >= mStartTs && u.start <= mEndTs) {
          count++;
          break;
        }
      }
    }

    return count;
  }

  // 월간 출석 수 계산 (토글에 따라 분기) //
  // oncePerDay=true:  고유 출석회원 수 (A_uniq, 참여 폭)
  // oncePerDay=false: 총 출석 횟수 (A_raw, 참여 빈도)
  function getMonthlyAttendedCount(attendanceMembers, year, month, oncePerDay) {
    const monthStr = String(year) + '-' + String(month).padStart(2, '0');
    let count = 0;

    if (oncePerDay) {
      for (const m of attendanceMembers) {
        for (const r of m.reserveAttendances) {
          if (r.classDate.substring(0, 7) === monthStr) {
            count++;
            break;
          }
        }
      }
    }
    else {
      for (const m of attendanceMembers) {
        for (const r of m.reserveAttendances) {
          if (r.classDate.substring(0, 7) === monthStr) {
            count++;
          }
        }
      }
    }

    return count;
  }

  // 월간 출석률: R_month = A_month / U_month × 100 //
  // 분모(U_month): 토글 무관 고정 //
  // 분자(A_month): 토글 ON=고유회원 수, OFF=총 출석 횟수 //
  function calcMonthlyRate(attendanceMembers, year, month, oncePerDay) {
    const validCount = getMonthlyValidCount(year, month);
    const attendedCount = getMonthlyAttendedCount(attendanceMembers, year, month, oncePerDay);

    if (validCount === 0) return 0;
    return (attendedCount / validCount) * 100;
  }

  // 월간 평균 출석률: (1/M) Σ R_month //
  function calcMonthlyAverageRate(attendanceMembers, year, oncePerDay) {
    const now = moment();
    const lastMonth = now.year() === year ? now.month() + 1 : 12;
    let sumOfRates = 0;
    let monthCount = 0;

    for (let m = 1; m <= lastMonth; m++) {
      const validCount = getMonthlyValidCount(year, m);

      if (validCount === 0) continue;

      sumOfRates += calcMonthlyRate(attendanceMembers, year, m, oncePerDay);
      monthCount++;
    }

    if (monthCount === 0) return 0;
    return sumOfRates / monthCount;
  }

  // 평균 출석률 계산 //
  // 일간: (1/D) Σ(a_i / N_i) × 100 //
  // 월간: (1/M) Σ(A_month / U_month × 100) //
  //   토글 ON: A_month = 고유 출석회원 수 (0~100%) //
  //   토글 OFF: A_month = 총 출석 횟수 (100% 초과 가능) //
  function calcAttendanceRate() {
    if (viewMode === 'monthly') {
      return Math.round(calcMonthlyAverageRate(yearlyAttendanceData, today.year(), isCountOncePerDay));
    }

    if (!startDate || !endDate) return 0;
    return Math.round(calcDailyAverageRate(attendanceTotal, startDate, endDate, isCountOncePerDay));
  }

  // 평균 출석 인원 수 //
  // 일간: 각 날짜별 출석 인원의 평균 //
  // 월간: 각 월별 출석 인원의 평균 //
  function calcAttendedCount() {
    if (viewMode === 'monthly') {
      const now = moment();
      const lastMonth = now.year() === today.year() ? now.month() + 1 : 12;
      let totalAttendees = 0;
      let monthCount = 0;

      for (let m = 1; m <= lastMonth; m++) {
        const validCount = getMonthlyValidCount(today.year(), m);
        const attendedCount = getMonthlyAttendedCount(yearlyAttendanceData, today.year(), m, isCountOncePerDay);

        if (validCount > 0) {
          totalAttendees += attendedCount;
          monthCount++;
        }
      }

      if (monthCount === 0) return 0;
      return Math.round(totalAttendees / monthCount);
    }
    else {
      if (!startDate || !endDate) return 0;

      const now = moment();
      const lastDay = moment.min(moment(endDate), now);
      const current = moment(startDate).clone();
      let totalAttendees = 0;
      let dayCount = 0;

      while (current.isSameOrBefore(lastDay, 'day')) {
        const { attendees, validMembers } = getDailyStats(attendanceTotal, current, isCountOncePerDay);

        if (validMembers > 0) {
          totalAttendees += attendees;
          dayCount++;
        }

        current.add(1, 'day');
      }

      if (dayCount === 0) return 0;
      return Math.round(totalAttendees / dayCount);
    }
  }

  // 일별 1회만 집계한 출석 랭킹 계산 //
  function calculateOncePerDayRanking(ranking) {
    const result = ranking.map(member => {
      const uniqueDates = new Set();

      for (const attendance of member.reserveAttendances) {
        const dateOnly = attendance.classDate.substring(0, 10);
        uniqueDates.add(dateOnly);
      }

      return { ...member, attendanceCount: uniqueDates.size };
    });

    result.sort((a, b) => b.attendanceCount - a.attendanceCount);

    return result;
  }

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
        isOpenPartnerGymSales ?
          <ContentWrapper>
            <HeaderWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text} cursor='default'>{calcDateText() + ' '}</Text>
            </HeaderWrapper>

            {/* 섹션 1: 출석률 차트 + 스크롤바 + 토글 */}
            <SectionWrapper>
                <HeaderRow>
                  <HeaderLeft>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>{viewMode === 'monthly' ? t('statistics.attendance.monthlyAverageAttendanceRate') : t('statistics.attendance.dailyAverageAttendanceRate')}</Text>
                    <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.subKey} cursor='default'>{calcAttendanceRate()}%</Text>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.subKey} cursor='default'>[{t('statistics.attendance.average') + ' ' + calcAttendedCount() + t('statistics.attendance.attendedSuffix')}]</Text>
                  </HeaderLeft>
                  <OncePerDayWrapper onClick={() => setIsCountOncePerDay(!isCountOncePerDay)}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='pointer'>{t('statistics.attendance.oncePerDay')}</Text>
                    <CheckButton $isClicked={isCountOncePerDay}>
                      <StyledCheck $isClicked={isCountOncePerDay} />
                    </CheckButton>
                  </OncePerDayWrapper>
                </HeaderRow>

                <AttendanceChart
                  chartData={chartData}
                  viewMode={viewMode}
                  isCountOncePerDay={isCountOncePerDay}
                />

                {/* 일간/월간 토글 */}
                <ToggleWrapper>
                    <ToggleButton
                      $isActive={viewMode === 'daily'}
                      onClick={() => setViewMode('daily')}>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={viewMode === 'daily' ? theme.subBackground : theme.text} cursor='pointer'>
                        {t('statistics.attendance.daily')}
                      </Text>
                    </ToggleButton>
                    <ToggleButton
                      $isActive={viewMode === 'monthly'}
                      onClick={() => setViewMode('monthly')}>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={viewMode === 'monthly' ? theme.subBackground : theme.text} cursor='pointer'>
                        {t('statistics.attendance.monthly')}
                      </Text>
                    </ToggleButton>
                </ToggleWrapper>
            </SectionWrapper>

            {/* 섹션 2: 수업별 수강인원 */}
            <SectionWrapper>
              <ClassAttendanceList
                frequencyData={frequencyData}
                calcDateText={calcDateText}
              />
            </SectionWrapper>

            {/* 섹션 3: 출석 순위 */}
            <SectionWrapper>
              <AttendanceRankingList
                data={processedAttendanceRanking}
                selectedPartnerGym={selectedPartnerGym}
                calcDateText={calcDateText}
              />
            </SectionWrapper>
          </ContentWrapper>
          :
          <NotOpenTextWrapper>
            <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText} cursor='default'>
              {t('statistics.partnerGymPrivate')}
            </Text>
            <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText} cursor='default'>
              {t('statistics.partnerGymPrivateDesc')}
            </Text>
          </NotOpenTextWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin: 38px 0;
  padding: 20px 40px;

  width: 960px;

  border-radius: 20px;
  box-shadow: ${props => props.$isDark ? 'none' : '0 40px 50px rgba(240, 232, 227, 0.5)'};
  background-color: ${props => props.theme.subBackground};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: 30px;
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;
`;

const OncePerDayWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 12px;

  &:hover {
    cursor: pointer;
  }
`;

const CheckButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 21px;
  height: 21px;

  border: ${props => props.$isClicked ? `1px solid ${props.theme.subKey}` : `1px solid ${props.theme.line}`};
  border-radius: 4px;
  background-color: ${props => props.$isClicked ? props.theme.subKey : props.theme.subBackground};

  &:hover {
    cursor: pointer;
  }

  transition: all 0.3s ease-in-out;
`;

const StyledCheck = styled(CheckSvg)`
  flex-shrink: 0;
  width: 12px;
  height: 10px;

  opacity: ${props => props.$isClicked ? 1 : 0};

  & path {
    fill: ${props => props.theme.subBackground};
  }

  transition: all 0.3s ease-in-out;
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 400px;

  background-color: ${props => props.theme.subBackground};
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin: 20px auto;

  width: 211px;
  height: 41px;

  gap: 5px;

  border-radius: 8px;
  background-color: ${props => props.theme.buttonFill};
`;

const ToggleButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 96px;
  height: 29px;

  border-radius: 6px;
  background-color: ${props => props.$isActive ? props.theme.subKey : 'transparent'};
  box-shadow: ${props => props.$isActive ? '0px 9px 7.1px rgba(0, 0, 0, 0.06)' : 'none'};

  &:hover {
    cursor: pointer;
  }

  transition: all 0.3s ease-in-out;
`;

const NotOpenTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 200px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;